import Head from 'next/head'
import Script from 'next/script'
import { BlogHeaderLinks } from '../../../../components/blogs/BlogHeaderLinks'
import { SITE_URL } from '../../../../utils/sitemap-config'
import { breadcrumbJsonLd } from '../../../../utils/breadcrumb'
import { blogsMaster } from '../../../../utils/sg-blogs-data'
import { getWpNewPostsByCategory, getWpPostsByCategory } from '../../../../lib/service'
import { HvacServices } from '../../../../components/blogs/HvacServices'

export async function getStaticProps() {
    const latestBlogs = blogsMaster['hvac-services'].blogArray;

    const wpPosts = await getWpPostsByCategory(100, "hvac-services");
    const wpNewPosts = await getWpNewPostsByCategory(100, "hvac-services"); // retrieve first 100 posts

  return {
      props: {
        latestBlogs,
        wpPosts,
        wpNewPosts
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 3 days
      revalidate: 259200, // In seconds
  }
}

const PAGE_URL = `${SITE_URL}/sg/blogs/hvac-services`
const PAGE_TITLE = 'Aircon Guides & Tips Singapore | StringsSG'
const PAGE_H1 = 'Aircon Guides & Tips for Singapore Homes'
const PAGE_DESCRIPTION = 'Aircon guides for Singapore homes: how often to service, chemical wash vs overhaul, gas top up, leaks, not cooling, repair costs, energy use and choosing an aircon.'
const OG_IMAGE = 'https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/living/aircon-installation.jpg'

// This is a listing page, so it is a CollectionPage with an ItemList of the
// articles - it used to be marked up as a single BlogPosting, which it isn't.
// Items follow the grid's order in BlogsList: new WP posts, in-repo guides,
// then legacy WP posts, de-duplicated by URL.
const buildJsonLd = (latestBlogs, wpPosts, wpNewPosts) => {
    const wpItem = (post) => ({ url: `${SITE_URL}/sg/aircon-servicing-singapore/${post.slug}`, name: post.title })
    const seen = new Set()
    const items = [
        ...(wpNewPosts ?? []).map(wpItem),
        ...(latestBlogs ?? []).map((blog) => ({ url: SITE_URL + blog.url, name: blog.title })),
        ...(wpPosts ?? []).map(wpItem),
    ].filter((item) => item.name && !seen.has(item.url) && seen.add(item.url))

    // Trail is set by hand: /sg/blogs/hvac-services sits outside the hub path,
    // so buildBreadcrumb would fall back to Home > Sg > Blogs.
    const trail = [
        { name: 'Home', url: `${SITE_URL}/` },
        { name: 'Aircon Servicing Singapore', url: `${SITE_URL}/sg/aircon-servicing-singapore` },
        { name: 'Aircon Guides', url: PAGE_URL },
    ]

    return {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'CollectionPage',
                '@id': `${PAGE_URL}/#webpage`,
                url: PAGE_URL,
                name: PAGE_TITLE,
                description: PAGE_DESCRIPTION,
                inLanguage: 'en-SG',
                isPartOf: { '@id': `${SITE_URL}/#website` },
                breadcrumb: { '@id': `${PAGE_URL}/#breadcrumblist` },
                mainEntity: {
                    '@type': 'ItemList',
                    numberOfItems: items.length,
                    itemListElement: items.map((item, i) => ({
                        '@type': 'ListItem',
                        position: i + 1,
                        url: item.url,
                        name: item.name,
                    })),
                },
            },
            breadcrumbJsonLd(trail, PAGE_URL),
        ],
    }
}

export default function Home(props) {
    const latestBlogs = props.latestBlogs
    const wpPosts = props.wpPosts
    const wpNewPosts = props.wpNewPosts

  return (
    <>
      <Head>
        <title>{PAGE_TITLE}</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content={PAGE_DESCRIPTION}/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={PAGE_TITLE}/>
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={OG_IMAGE} />
        <meta name="twitter:site" content="@stringssg"/>
        <meta name="twitter:url" content={PAGE_URL}/>
        <meta name="twitter:title" content={PAGE_TITLE}/>
        <meta name="twitter:description" content={PAGE_DESCRIPTION}/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href={PAGE_URL} />
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(buildJsonLd(latestBlogs, wpPosts, wpNewPosts)) }}
        />
      </Head>
      <div className='max-w-5xl mx-auto py-10'>
            <HvacServices title={PAGE_H1} latestBlogs={latestBlogs} wpPosts={wpPosts} wpNewPosts={wpNewPosts}/>
      </div>
      <h2 className='bg-gray-800 text-white py-4 p-2 text-center text-2xl'>
          Topics
      </h2>
      <BlogHeaderLinks/>
      <Script 
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {              
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', 'G-QD1HGGHDYJ');
          }} 
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}
